import { call, put, takeEvery, CallEffect, PutEffect } from 'redux-saga/effects'
import { authActions } from './actions'
import { authService } from '../../../services'
import { AUTH_TOKEN_KEY, USER_STORAGE_KEY, CHECKOUT_STORAGE_KEY, USER_LOGOUT_EVENT } from '../../../constants/storageKeys'

/**
 * Saga para verificar autenticação ao carregar a aplicação
 */
function* checkAuthSaga(): Generator<CallEffect | PutEffect, void, any> {
  try {
    // Verificar se há token
    if (authService.isAuthenticated()) {
      // Buscar dados atualizados do usuário
      const response = yield call(authService.getMe)
      
      const token = authService.getToken()
      
      if (response.user && token) {
        yield put(authActions.checkAuthSuccess({
          user: response.user,
          token
        }))
      } else {
        yield put(authActions.checkAuthFailure())
      }
    } else {
      yield put(authActions.checkAuthFailure())
    }
  } catch (error: any) {
    console.error('Erro ao verificar autenticação:', error)
    // Limpar dados em caso de erro
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(USER_STORAGE_KEY)
    yield put(authActions.checkAuthFailure())
  }
}

/**
 * Saga para fazer login
 */
function* loginSaga(action: ReturnType<typeof authActions.loginRequest>): Generator<CallEffect | PutEffect, void, any> {
  try {
    const { emailOrCpf, password } = action.payload
    const response = yield call(authService.login, { emailOrCpf, password })
    
    if (response.user && response.token) {
      yield put(authActions.loginSuccess({
        user: response.user,
        token: response.token
      }))
    } else {
      yield put(authActions.loginFailure({
        error: 'Erro ao fazer login. Tente novamente.'
      }))
    }
  } catch (error: any) {
    // O interceptor pode transformar o erro, então verificamos múltiplos lugares
    let errorMessage = 'Erro ao fazer login. Tente novamente.'
    
    if (error) {
      // Mensagem já extraída pelo interceptor
      if (error.message && error.message !== 'Unauthorized') {
        errorMessage = error.message
      }
      // Mensagem direta do response da API
      else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }
      // Mensagem em error do response
      else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }
      // Status 401 geralmente significa credenciais inválidas
      else if (error.response?.status === 401 || error.status === 401) {
        errorMessage = 'Credenciais inválidas. Verifique seu email/CPF/CNPJ e senha.'
      }
      // Outras mensagens
      else if (typeof error === 'string') {
        errorMessage = error
      }
    }
    
    yield put(authActions.loginFailure({ error: errorMessage }))
  }
}

/**
 * Saga para fazer logout
 */
function* logoutSaga(): Generator<CallEffect | PutEffect, void, any> {
  try {
    yield call(authService.logout)
    
    // Limpar checkout storage
    localStorage.removeItem(CHECKOUT_STORAGE_KEY)
    
    // Emitir evento de logout
    window.dispatchEvent(new CustomEvent(USER_LOGOUT_EVENT))
    
    yield put(authActions.logoutSuccess())
  } catch (error: any) {
    console.error('Erro no logout:', error)
    
    // Mesmo com erro, limpar dados locais
    localStorage.removeItem(CHECKOUT_STORAGE_KEY)
    window.dispatchEvent(new CustomEvent(USER_LOGOUT_EVENT))
    
    yield put(authActions.logoutFailure({
      error: error.message || 'Erro ao fazer logout'
    }))
  }
}

/**
 * Saga para registrar novo usuário
 */
function* registerSaga(action: ReturnType<typeof authActions.registerRequest>): Generator<CallEffect | PutEffect, void, any> {
  try {
    const userData = action.payload
    
    const response = yield call(authService.register, {
      name: userData.name,
      email: userData.email,
      cpf: userData.cpf,
      password: userData.password,
      phone: userData.phone || '',
      birthDate: userData.birthDate
    })
    
    if (response.user && response.token) {
      yield put(authActions.registerSuccess({
        user: response.user,
        token: response.token
      }))
    } else {
      yield put(authActions.registerFailure({
        error: 'Erro ao registrar usuário. Tente novamente.'
      }))
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao registrar usuário. Tente novamente.'
    yield put(authActions.registerFailure({ error: errorMessage }))
  }
}

/**
 * Saga para buscar dados atualizados do usuário
 */
function* getMeSaga(): Generator<CallEffect | PutEffect, void, any> {
  try {
    const response = yield call(authService.getMe)
    
    if (response.user) {
      yield put(authActions.getMeSuccess({ user: response.user }))
    } else {
      yield put(authActions.getMeFailure({
        error: 'Erro ao buscar dados do usuário'
      }))
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar dados do usuário'
    yield put(authActions.getMeFailure({ error: errorMessage }))
  }
}

/**
 * Root saga do módulo Auth
 */
export function* authSaga() {
  yield takeEvery(authActions.checkAuthRequest.type, checkAuthSaga)
  yield takeEvery(authActions.loginRequest.type, loginSaga)
  yield takeEvery(authActions.logoutRequest.type, logoutSaga)
  yield takeEvery(authActions.registerRequest.type, registerSaga)
  yield takeEvery(authActions.getMeRequest.type, getMeSaga)
}

